// src/pages/RentalPortalPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "../../gmanewstyle.module.css";
import logoimage from "../assets/Group -2.png";
import { format } from "date-fns";
import PortalSidebar, {
  usePortalTab,
} from "../PortalSidebar/PortalSidebar";

const Pagination = ({
  currentPage,
  setCurrentPage,
  totalRecords,
  recordsPerPage,
}) => {
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = Math.min(startIndex + recordsPerPage, totalRecords);

  return (
    <div className={styles.pagination}>
      <button
        className={`${styles.pageBtn} ${styles.pageBtnPrimary}`}
        onClick={() => setCurrentPage(currentPage - 1)}
        disabled={currentPage === 1}
      >
        Previous
      </button>

      <span className={styles.pageMeta}>
        {totalRecords === 0
          ? "0 - 0 of 0"
          : `${startIndex + 1} - ${endIndex} of ${totalRecords}`}
      </span>

      <button
        className={`${styles.pageBtn} ${styles.pageBtnPrimary}`}
        onClick={() => setCurrentPage(currentPage + 1)}
        disabled={endIndex >= totalRecords}
      >
        Next
      </button>
    </div>
  );
};

const RentalPortalPage = () => {
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loadingAccess, setLoadingAccess] = useState(true);
  const [userAccess, setUserAccess] = useState([]);
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

 const [portalMode, setPortalMode] = usePortalTab("rental");

  const [subFilter, setSubFilter] = useState("current");
  const [agreements, setAgreements] = useState([]);
  const [rentalUnits, setRentalUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [pendingDateFilter, setPendingDateFilter] = useState({
    from: "",
    to: "",
  });
  const [dateFilter, setDateFilter] = useState({ from: "", to: "" });
  const [dateMode, setDateMode] = useState("single");

  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  useEffect(() => {
  if (portalMode) {
    localStorage.setItem("rental_portal_mode", portalMode);
  }
}, [portalMode]);

useEffect(() => {
  if (loadingAccess) return;

  if (
    portalMode === "landlord" &&
    !userAccess.includes("landlord") &&
    userAccess.includes("tenant")
  ) {
    setPortalMode("tenant");
  } else if (
    portalMode === "tenant" &&
    !userAccess.includes("tenant") &&
    userAccess.includes("landlord")
  ) {
    setPortalMode("landlord");
  }
}, [loadingAccess, portalMode, setPortalMode, userAccess]);

  useEffect(() => {
    document.title = "GMA Portal || Rental Portal";

    const handleResize = () => {
      const desktop = window.innerWidth >= 1024;
      setIsDesktop(desktop);

      if (desktop) {
        setSidebarOpen(true);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    function handleClick(e) {
      if (isDesktop) return;

      const sidebar = document.getElementsByClassName(styles.sidebar)[0];
      const hamburger = document.getElementsByClassName(styles.menuBtn)[0];

      if (!sidebar || !hamburger) return;

      const clickedInsideSidebar = sidebar.contains(e.target);
      const clickedHamburger = hamburger.contains(e.target);

      if (sidebarOpen && !clickedInsideSidebar && !clickedHamburger) {
        setSidebarOpen(false);
      }
    }

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [sidebarOpen, isDesktop]);

  const formatDateSafe = (rawDate) => {
    if (!rawDate) return "";
    try {
      return format(new Date(rawDate), "dd MMM yyyy");
    } catch {
      return rawDate;
    }
  };

  const formatCurrencySafe = (value) => {
    if (value === null || value === undefined || value === "") return "R0.00";
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return `R${num.toLocaleString("en-ZA", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const getLandlordAccountName = (item) => {
    return (
      item.lessor_account_name ||
      item[
        "_cj_landlordaccount_value@OData.Community.Display.V1.FormattedValue"
      ] ||
      item["_cj_lessoraccount_value_formatted"] ||
      item.landlord_account_name ||
      "Unknown Landlord Account"
    );
  };

  const getLandlordAccountId = (item) => {
    return (
      item._cj_lessoraccount_value ||
      item.lessor_account_id ||
      item.landlord_account_id ||
      getLandlordAccountName(item)
    );
  };

  const getRentalUnitId = (item) => {
    return (
      item.cj_rentalunitid ||
      item.rental_unit_id ||
      item.rentalunitid ||
      item.id ||
      null
    );
  };

  const matchesDateFilter = (dateStr) => {
    if (!dateFilter.from || !dateStr) return true;

    const itemDate = format(new Date(dateStr), "yyyy-MM-dd");

    if (dateMode === "single") {
      return itemDate === format(new Date(dateFilter.from), "yyyy-MM-dd");
    }

    return (
      itemDate >= dateFilter.from &&
      itemDate <= (dateFilter.to || dateFilter.from)
    );
  };

  useEffect(() => {
    const fetchUserAccess = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }

      try {
        const decoded = JSON.parse(atob(token.split(".")[1]));
        const { email, contact_id, firstname, lastname, name } = decoded || {};

        const fullName =
          firstname && lastname
            ? `${firstname} ${lastname}`
            : name
              ? name
              : email || "User";

        setDisplayName(fullName);

        if (!email) {
          setLoadingAccess(false);
          return;
        }

        const resp = await fetch(
          "https://portal.gmacc.co.za/api/contact/determine-access/",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ email, contact_id }),
          },
        );

        const data = await resp.json();

        if (resp.ok) {
          const access = data.access || [];
          setUserAccess(access);

          if (portalMode === "landlord" && !access.includes("landlord")) {
            setError("You do not have landlord access.");
          }

          if (portalMode === "tenant" && !access.includes("tenant")) {
            setError("You do not have tenant access.");
          }
        } else {
          setError(data.error || "Failed to determine user access.");
        }
      } catch (err) {
        console.error("Failed to fetch access:", err);
        setError("Failed to determine user access.");
      } finally {
        setLoadingAccess(false);
      }
    };

    fetchUserAccess();
  }, [navigate, portalMode]);

  const fetchRentalAgreements = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const resp = await fetch("https://portal.gmacc.co.za/api/rental/agreements/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          portal_mode: portalMode,
          view_type: "current",
        }),
      });

      const data = await resp.json();

      if (resp.ok) {
        setAgreements(data.results || []);
      } else {
        setAgreements([]);
        setError(data.error || "Failed to load rental agreements.");
      }
    } catch (err) {
      console.error("Failed to load rental agreements:", err);
      setAgreements([]);
      setError("Failed to load rental agreements.");
    } finally {
      setLoading(false);
    }
  };

  const fetchRentalUnits = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const resp = await fetch("https://portal.gmacc.co.za/api/rental/units/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          portal_mode: portalMode,
          view_type: "active_units",
        }),
      });

      const data = await resp.json();

      if (resp.ok) {
        setRentalUnits(data.results || []);
      } else {
        setRentalUnits([]);
        setError(data.error || "Failed to load rental units.");
      }
    } catch (err) {
      console.error("Failed to load rental units:", err);
      setRentalUnits([]);
      setError("Failed to load rental units.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loadingAccess) return;

    if (
      (portalMode === "landlord" && !userAccess.includes("landlord")) ||
      (portalMode === "tenant" && !userAccess.includes("tenant"))
    ) {
      return;
    }

    if (subFilter === "active_units") {
      fetchRentalUnits();
    } else {
      fetchRentalAgreements();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingAccess, portalMode, subFilter, userAccess]);

  useEffect(() => {
    setSearchTerm("");
    setDateFilter({ from: "", to: "" });
    setPendingDateFilter({ from: "", to: "" });
    setCurrentPage(1);
    setExpandedGroupId(null);
  }, [subFilter, portalMode]);


  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("contact_id");
    localStorage.removeItem("rental_portal_mode");
    navigate("/#/");
  };

  const toggleExpand = (id) => {
    setExpandedGroupId((prev) => (prev === id ? null : id));
  };

  const handleRentalUnitClick = (unit) => {
    const rentalUnitId = getRentalUnitId(unit);
    if (!rentalUnitId) {
      console.warn("No rental unit id found on clicked row:", unit);
      return;
    }

    navigate(`/rental-unit/${rentalUnitId}`);
  };

  const filteredAgreements = useMemo(() => {
    return (agreements || []).filter((item) => {
      const agreementName = String(item.cj_name || "").toLowerCase();
      const unitName = String(item.rental_unit_name || "").toLowerCase();
      const partyName =
        portalMode === "landlord"
          ? String(item.lessee_account_name || "").toLowerCase()
          : String(item.lessor_account_name || "").toLowerCase();

      const matchesSearch =
        agreementName.includes(searchTerm.toLowerCase()) ||
        unitName.includes(searchTerm.toLowerCase()) ||
        partyName.includes(searchTerm.toLowerCase());

      return matchesSearch && matchesDateFilter(item.cj_startdate);
    });
  }, [agreements, searchTerm, dateFilter, dateMode, portalMode]);

  const filteredRentalUnits = useMemo(() => {
    return (rentalUnits || []).filter((item) => {
      const unitName = String(item.cj_name || "").toLowerCase();
      const size = String(item.cj_size || "").toLowerCase();
      const occupationStatus = String(
        item["cj_occupationstatus@OData.Community.Display.V1.FormattedValue"] ||
          "",
      ).toLowerCase();
      const landlordName = String(
        getLandlordAccountName(item) || "",
      ).toLowerCase();

      const matchesSearch =
        unitName.includes(searchTerm.toLowerCase()) ||
        size.includes(searchTerm.toLowerCase()) ||
        occupationStatus.includes(searchTerm.toLowerCase()) ||
        landlordName.includes(searchTerm.toLowerCase());

      return matchesSearch;
    });
  }, [rentalUnits, searchTerm]);

  const groupedAgreements = useMemo(() => {
    const groups = {};

    filteredAgreements.forEach((item) => {
      const key =
        portalMode === "landlord"
          ? item._cj_lessoraccount_value ||
            item.lessor_account_id ||
            item.lessor_account_name ||
            "unknown-landlord"
          : item._cj_lesseeaccount_value ||
            item.lessee_account_id ||
            item.lessee_account_name ||
            "unknown-tenant";

      const name =
        portalMode === "landlord"
          ? item.lessor_account_name ||
            item[
              "_cj_lessoraccount_value@OData.Community.Display.V1.FormattedValue"
            ] ||
            "Unknown Landlord Account"
          : item.lessee_account_name || "Unknown Tenant Account";

      if (!groups[key]) {
        groups[key] = {
          id: key,
          accountName: name,
          items: [],
        };
      }

      groups[key].items.push(item);
    });

    return Object.values(groups);
  }, [filteredAgreements, portalMode]);

  const groupedRentalUnits = useMemo(() => {
    const groups = {};

    filteredRentalUnits.forEach((item) => {
      const key = getLandlordAccountId(item);
      const name = getLandlordAccountName(item);

      if (!groups[key]) {
        groups[key] = {
          id: key,
          accountName: name,
          items: [],
        };
      }

      groups[key].items.push(item);
    });

    return Object.values(groups);
  }, [filteredRentalUnits]);

  const activeGroups =
    subFilter === "active_units" ? groupedRentalUnits : groupedAgreements;

  useEffect(() => {
    setExpandedGroupId(null);
    setCurrentPage(1);
  }, [portalMode, subFilter, searchTerm, dateFilter, dateMode]);

  const startIndex = (currentPage - 1) * recordsPerPage;
  const paginatedGroups = activeGroups.slice(
    startIndex,
    startIndex + recordsPerPage,
  );

  return (
    <div className={styles.page}>
      <header className={styles.topbar}>
        <div className={styles.topbarLeft}>
          <img src={logoimage} alt="GMA Logo" className={styles.topLogo} />

          <button
            type="button"
            className={styles.menuBtn}
            onClick={() => setSidebarOpen((v) => !v)}
          >
            ☰
          </button>

          <h1 className={styles.topTitle}>RENTAL PORTAL</h1>
        </div>

        <div
          className={styles.userChip}
          onClick={() => navigate("/user-profile")}
          style={{ cursor: "pointer" }}
        >
          <span className={styles.statusDot}></span>
          <span className={styles.userName}>{displayName} ▾</span>
        </div>
      </header>

      <div className={styles.shell}>
        <PortalSidebar
  activePortal="rental"
  activeTab={portalMode}
  onTabChange={setPortalMode}
  userAccess={userAccess}
  loadingAccess={loadingAccess}
  sidebarOpen={sidebarOpen}
  hiddenTabKeys={[
    ...(!userAccess.includes("landlord") ? ["landlord"] : []),
    ...(!userAccess.includes("tenant") ? ["tenant"] : []),
  ]}
  ticketRole={null}
  onLogout={handleLogout}
/>

        <main className={styles.contentArea}>
          <div className={styles.panel}>
            <div className={styles.subTabs}>
              <button
                className={`${styles.subTab} ${
                  subFilter === "current" ? styles.subTabActive : ""
                }`}
                onClick={() => setSubFilter("current")}
              >
                CURRENT
              </button>

              {portalMode === "landlord" && (
                <button
                  className={`${styles.subTab} ${
                    subFilter === "active_units" ? styles.subTabActive : ""
                  }`}
                  onClick={() => setSubFilter("active_units")}
                >
                  ACTIVE UNITS
                </button>
              )}
            </div>

            <p className={styles.maintp}>
              {portalMode === "landlord" &&
                subFilter === "current" &&
                "These are the currently active rental agreements linked to your landlord account(s)."}

              {portalMode === "landlord" &&
                subFilter === "active_units" &&
                "These are the active rental units linked to your landlord account(s)."}

              {portalMode === "tenant" &&
                "These are the currently active rental agreements linked to your tenant account(s)."}
            </p>

            <div className={styles.filterRow}>
              <input
                type="text"
                placeholder={
                  subFilter === "active_units"
                    ? "Search Unit / Size / Occupation Status / Account"
                    : "Search Agreement / Unit / Account"
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />

              {subFilter !== "active_units" && (
                <>
                  <select
                    className={styles.select}
                    value={dateMode}
                    onChange={(e) => {
                      setDateMode(e.target.value);
                      setPendingDateFilter({ from: "", to: "" });
                    }}
                  >
                    <option value="single">Single Date</option>
                    <option value="range">Date Range</option>
                  </select>

                  <div className={styles.dateGroup}>
                    <input
                      type="date"
                      className={styles.dateInput}
                      value={pendingDateFilter.from}
                      onChange={(e) =>
                        setPendingDateFilter((pd) => ({
                          ...pd,
                          from: e.target.value,
                        }))
                      }
                    />

                    {dateMode === "range" && (
                      <input
                        type="date"
                        className={styles.dateInput}
                        min={pendingDateFilter.from}
                        value={pendingDateFilter.to}
                        onChange={(e) =>
                          setPendingDateFilter((pd) => ({
                            ...pd,
                            to: e.target.value,
                          }))
                        }
                      />
                    )}
                  </div>

                  <button
                    className={styles.confirmBtn}
                    onClick={() => {
                      if (dateMode === "single" && pendingDateFilter.from) {
                        setDateFilter({ from: pendingDateFilter.from });
                      } else if (
                        dateMode === "range" &&
                        pendingDateFilter.from &&
                        pendingDateFilter.to
                      ) {
                        if (
                          new Date(pendingDateFilter.from) >
                          new Date(pendingDateFilter.to)
                        ) {
                          alert("From date cannot be after To date");
                          return;
                        }

                        setDateFilter({
                          from: pendingDateFilter.from,
                          to: pendingDateFilter.to,
                        });
                      }
                    }}
                  >
                    Confirm Date
                  </button>
                </>
              )}

              <button
                className={styles.clearBtn}
                onClick={() => {
                  setSearchTerm("");
                  setDateFilter({ from: "", to: "" });
                  setPendingDateFilter({ from: "", to: "" });
                }}
              >
                Clear Filters
              </button>
            </div>

            {loading || loadingAccess ? (
              <div className={styles.loadingContainer}>
                <div className={styles.loadingImage}></div>
                <p className={styles.loadingText}>Loading...</p>
              </div>
            ) : error ? (
              <p className={styles.errorText}>{error}</p>
            ) : activeGroups.length === 0 ? (
              <p className={styles.errorText}>
                {subFilter === "active_units"
                  ? "No Active Rental Units Found."
                  : "No Rental Agreements Found."}
              </p>
            ) : (
              <>
                {paginatedGroups.map((group) => (
                  <div
                    key={group.id}
                    className={`${styles.expandableDiv} ${
                      expandedGroupId === group.id ? styles.expanded : ""
                    }`}
                  >
                    <div
                      className={styles.divHeader}
                      onClick={() => toggleExpand(group.id)}
                      style={{ cursor: "pointer" }}
                    >
                      <div className={styles.section}>
                        <span>
                          {subFilter === "active_units"
                            ? "Landlord Account:"
                            : portalMode === "landlord"
                              ? "Landlord Account:"
                              : "Tenant Account:"}
                        </span>
                        <span>{group.accountName}</span>
                      </div>

                      <div className={styles.section}>
                        <span>
                          {subFilter === "active_units"
                            ? "Units:"
                            : "Agreements:"}
                        </span>
                        <span>{group.items.length}</span>
                      </div>

                      <div
                        className={styles.actions}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          className={styles.pillHeaderBtnLight}
                          onClick={() => toggleExpand(group.id)}
                        >
                          {expandedGroupId === group.id
                            ? "Show less ▲"
                            : "Show more ▼"}
                        </button>
                      </div>
                    </div>

                    {expandedGroupId === group.id && (
                      <>
                        <div className={styles.separator} />
                        <div className={styles.responsiveTableWrapper}>
                          {subFilter === "active_units" ? (
                            <table className={styles.dataTable}>
                              <thead>
                                <tr>
                                  <th>Unit Name</th>
                                  <th>Size</th>
                                  <th>Occupation Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {group.items.map((item) => (
                                  <tr
                                    key={getRentalUnitId(item) || item.cj_name}
                                    onClick={() => handleRentalUnitClick(item)}
                                    className={styles.clickableRow}
                                    style={{ cursor: "pointer" }}
                                  >
                                    <td
                                      className={styles.clickableUnitName}
                                      onMouseEnter={(e) =>
                                        (e.currentTarget.style.textDecoration =
                                          "underline")
                                      }
                                      onMouseLeave={(e) =>
                                        (e.currentTarget.style.textDecoration =
                                          "none")
                                      }
                                    >
                                      {item.cj_name || ""}
                                    </td>
                                    <td>{item.cj_size || ""}</td>
                                    <td>
                                      {item[
                                        "cj_occupationstatus@OData.Community.Display.V1.FormattedValue"
                                      ] || ""}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <table className={styles.dataTable}>
                              <thead>
                                <tr>
                                  <th>Agreement Name</th>
                                  <th>Start Date</th>
                                  <th>Agreement Term</th>
                                  <th>Rental Unit</th>
                                  <th>
                                    {portalMode === "landlord"
                                      ? "Tenant Account"
                                      : "Landlord Account"}
                                  </th>
                                  <th>Tenant Balance</th>
                                  <th>Monthly Rental</th>
                                </tr>
                              </thead>
                              <tbody>
                                {group.items.map((item) => (
                                  <tr key={item.cj_rentalagreementid}>
                                    <td>{item.cj_name || ""}</td>
                                    <td>{formatDateSafe(item.cj_startdate)}</td>
                                    <td>{item.cj_agreementterm || ""}</td>
                                    <td>{item.rental_unit_name || ""}</td>
                                    <td>
                                      {portalMode === "landlord"
                                        ? item.lessee_account_name || ""
                                        : item.lessor_account_name || ""}
                                    </td>
                                    <td>
                                      {formatCurrencySafe(
                                        item.cj_tenantaccountbalance,
                                      )}
                                    </td>
                                    <td>
                                      {formatCurrencySafe(
                                        item.cj_monthlyrental,
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}

                <Pagination
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  totalRecords={activeGroups.length}
                  recordsPerPage={recordsPerPage}
                />
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default RentalPortalPage;
